import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocales } from 'src/locales';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'notistack';
import { useGetAllVehicleTypesQuery } from 'src/redux/slices/vehicleSlice';
import { InfoBox } from 'src/components/map/InfoBox';
import ConfirmDialog from 'src/components/confirm-dialog';

import { VehicleTypeCostFee, VehicleTypeCostFeeDTO } from '../../../@types/vehicleCostFee';
import {
  useCreateVehicleTypeCostFeeMutation,
  useDeleteVehicleTypeCostFeeMutation,
  useGetVehicleTypeCostFeesByTourBundleQuery,
  useUpdateVehicleTypeCostFeeMutation,
} from '../../../redux/slices/vehicleTypeCostFeeSlice';

interface Props {
  tourBundleId: string;
}

export default function VehicleCostFeesManagement({ tourBundleId }: Props) {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [openVehicleTypeDialog, setOpenVehicleTypeDialog] = useState(false);
  const [editingVehicleTypeFee, setEditingVehicleTypeFee] = useState<VehicleTypeCostFee | null>(
    null
  );
  const [vehicleTypeFeeToDelete, setVehicleTypeFeeToDelete] = useState<VehicleTypeCostFee | null>(
    null
  );
  const [localVehicleTypeFees, setLocalVehicleTypeFees] = useState<VehicleTypeCostFeeDTO[]>([]);
  const [vehicleTypeFeeFormData, setVehicleTypeFeeFormData] = useState<
    Partial<VehicleTypeCostFeeDTO>
  >({
    vehicleTypeId: 0,
    costFee: 0,
    amountOfVehicles: 0,
    amountOfVehiclesFixed: false,
  });

  const { data: vehicleTypes } = useGetAllVehicleTypesQuery();

  const { data: vehicleTypeFees, isLoading: isLoadingVehicleTypeFees } =
    useGetVehicleTypeCostFeesByTourBundleQuery(tourBundleId);

  const [createVehicleTypeFee] = useCreateVehicleTypeCostFeeMutation();
  const [updateVehicleTypeFee] = useUpdateVehicleTypeCostFeeMutation();
  const [deleteVehicleTypeFee, { isLoading: isDeletingVehicleTypeFee }] =
    useDeleteVehicleTypeCostFeeMutation();

  const vehicleTypeFeesList = vehicleTypeFees ?? [];

  const handleOpenVehicleTypeDialog = (fee?: VehicleTypeCostFee) => {
    if (fee) {
      setEditingVehicleTypeFee(fee);
      setVehicleTypeFeeFormData({ ...fee });
    } else {
      setEditingVehicleTypeFee(null);
      setVehicleTypeFeeFormData({
        vehicleTypeId: 0,
        costFee: 0,
        amountOfVehicles: 0,
        amountOfVehiclesFixed: false,
      });
    }
    setOpenVehicleTypeDialog(true);
  };

  const handleCloseVehicleTypeDialog = () => {
    setOpenVehicleTypeDialog(false);
    setEditingVehicleTypeFee(null);
  };

  const handleSaveVehicleTypeFee = async () => {
    // Validate vehicleTypeId
    if (!vehicleTypeFeeFormData.vehicleTypeId || vehicleTypeFeeFormData.vehicleTypeId === 0) {
      enqueueSnackbar(`${translate('vehicleCostFees.selectVehicleType')}`, {
        variant: 'warning',
      });
      return;
    }

    // Check if vehicle type already exists (for new entries)
    if (!editingVehicleTypeFee) {
      const existsInSaved = vehicleTypeFeesList.some(
        (fee) => fee.vehicleTypeId === vehicleTypeFeeFormData.vehicleTypeId
      );
      const existsInLocal = localVehicleTypeFees.some(
        (fee) => fee.vehicleTypeId === vehicleTypeFeeFormData.vehicleTypeId
      );

      if (existsInSaved || existsInLocal) {
        enqueueSnackbar(`${translate('vehicleCostFees.vehicleTypeAlreadyExists')}`, {
          variant: 'warning',
        });
        return;
      }
    }

    // Validate costFee
    if (vehicleTypeFeeFormData.costFee === undefined || vehicleTypeFeeFormData.costFee < 0) {
      enqueueSnackbar(`${translate('vehicleCostFees.invalidCostFee')}`, {
        variant: 'warning',
      });
      return;
    }

    // Validate amountOfVehicles
    if (
      vehicleTypeFeeFormData.amountOfVehiclesFixed &&
      (vehicleTypeFeeFormData.amountOfVehicles === undefined ||
        vehicleTypeFeeFormData.amountOfVehicles < 0)
    ) {
      enqueueSnackbar(`${translate('vehicleCostFees.invalidAmountOfVehicles')}`, {
        variant: 'warning',
      });
      return;
    }

    if (editingVehicleTypeFee) {
      try {
        const updatePayload = {
          id: editingVehicleTypeFee.id,
          data: {
            vehicleTypeId: vehicleTypeFeeFormData.vehicleTypeId!,
            costFee: vehicleTypeFeeFormData.costFee!,
            amountOfVehicles: vehicleTypeFeeFormData.amountOfVehiclesFixed
              ? vehicleTypeFeeFormData.amountOfVehicles!
              : 0,
            amountOfVehiclesFixed: !!vehicleTypeFeeFormData.amountOfVehiclesFixed,
            tourBundleId: Number(tourBundleId),
            vehicleTypeName: vehicleTypeFeeFormData.vehicleTypeName,
          },
        };

        await updateVehicleTypeFee(updatePayload).unwrap();

        enqueueSnackbar(`${translate('vehicleCostFees.updateSuccess')}`, {
          variant: 'success',
        });
        handleCloseVehicleTypeDialog();
      } catch {
        enqueueSnackbar(`${translate('vehicleCostFees.updateError')}`, {
          variant: 'error',
        });
      }
    } else {
      const vehicleType = vehicleTypes?.find(
        (vt) => vt.id === vehicleTypeFeeFormData.vehicleTypeId?.toString()
      );

      const newFee: VehicleTypeCostFeeDTO = {
        vehicleTypeId: vehicleTypeFeeFormData.vehicleTypeId!,
        costFee: vehicleTypeFeeFormData.costFee!,
        amountOfVehicles: vehicleTypeFeeFormData.amountOfVehiclesFixed
          ? vehicleTypeFeeFormData.amountOfVehicles!
          : 0,
        amountOfVehiclesFixed: !!vehicleTypeFeeFormData.amountOfVehiclesFixed,
        tourBundleId: Number(tourBundleId),
        vehicleTypeName: vehicleType?.name || '',
      };

      setLocalVehicleTypeFees([...localVehicleTypeFees, newFee]);
      handleCloseVehicleTypeDialog();
      enqueueSnackbar(`${translate('vehicleCostFees.addedToList')}`, {
        variant: 'success',
      });
    }
  };

  const handleSaveAllVehicleTypeFees = async () => {
    if (localVehicleTypeFees.length === 0) {
      enqueueSnackbar(`${translate('vehicleCostFees.noFeesToSave')}`, {
        variant: 'warning',
      });
      return;
    }

    try {
      for (const fee of localVehicleTypeFees) {
        const createPayload: VehicleTypeCostFeeDTO = {
          vehicleTypeId: fee.vehicleTypeId,
          costFee: fee.costFee,
          amountOfVehicles: fee.amountOfVehicles,
          amountOfVehiclesFixed: fee.amountOfVehiclesFixed,
          tourBundleId: fee.tourBundleId,
          vehicleTypeName: fee.vehicleTypeName,
        };
        await createVehicleTypeFee(createPayload).unwrap();
      }

      enqueueSnackbar(
        `${translate('vehicleCostFees.allSavedSuccess', { count: localVehicleTypeFees.length })}`,
        { variant: 'success' }
      );
      setLocalVehicleTypeFees([]);
    } catch {
      enqueueSnackbar(`${translate('vehicleCostFees.saveError')}`, { variant: 'error' });
    }
  };

  const handleRemoveFromList = (id: number) => {
    setLocalVehicleTypeFees(localVehicleTypeFees.filter((fee) => fee.id !== id));
    enqueueSnackbar(`${translate('vehicleCostFees.removedFromList')}`, {
      variant: 'info',
    });
  };

  const handleDeleteVehicleTypeFee = async () => {
    if (!vehicleTypeFeeToDelete) {
      return;
    }

    try {
      await deleteVehicleTypeFee(vehicleTypeFeeToDelete.id).unwrap();
      setVehicleTypeFeeToDelete(null);
      enqueueSnackbar(`${translate('vehicleCostFees.deleteSuccess')}`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(`${translate('vehicleCostFees.deleteError')}`, { variant: 'error' });
    }
  };

  if (isLoadingVehicleTypeFees) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Vehicle Type Cost Fees Section */}
      <Card sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6">{`${translate('vehicleCostFees.title')}`}</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleOpenVehicleTypeDialog()}
            sx={{ textTransform: 'none' }}
          >
            {`${translate('vehicleCostFees.new')}`}
          </Button>
        </Stack>

        <InfoBox title={`${translate('vehicleCostFees.infoDescription')}`} />

        {/* Unsaved Vehicle Type Fees */}
        {localVehicleTypeFees.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2, color: 'warning.main' }}>
              {`${translate('vehicleCostFees.unsavedEntries')}`}
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: 'warning.lighter' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{`${translate('vehicleCostFees.vehicleType')}`}</TableCell>
                    <TableCell align="center">{`${translate('vehicleCostFees.costFee')}`}</TableCell>
                    <TableCell align="center">{`${translate('vehicleCostFees.amountOfVehicles')}`}</TableCell>
                    <TableCell align="center">{`${translate('vehicleCostFees.fixed')}`}</TableCell>
                    <TableCell align="center">{`${translate('actions')}`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localVehicleTypeFees.map((fee) => (
                    <TableRow key={fee.id}>
                      <TableCell>{fee.vehicleTypeName}</TableCell>
                      <TableCell align="center">{fee.costFee.toFixed(2)} €</TableCell>
                      <TableCell align="center">
                        {fee.amountOfVehiclesFixed ? fee.amountOfVehicles : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {fee.amountOfVehiclesFixed ? `${translate('yes')}` : `${translate('no')}`}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromList(fee.id!)}
                          color="error"
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Saved Vehicle Type Fees */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {`${translate('vehicleCostFees.existingFees')}`}
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{`${translate('vehicleCostFees.vehicleType')}`}</TableCell>
                <TableCell align="center">{`${translate('vehicleCostFees.costFee')}`}</TableCell>
                <TableCell align="center">{`${translate('vehicleCostFees.amountOfVehicles')}`}</TableCell>
                <TableCell align="center">{`${translate('vehicleCostFees.fixed')}`}</TableCell>
                <TableCell align="center">{`${translate('actions')}`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicleTypeFeesList.length > 0 ? (
                vehicleTypeFeesList.map((fee) => (
                  <TableRow key={fee.id}>
                    <TableCell>{fee.vehicleTypeName}</TableCell>
                    <TableCell align="center">{fee.costFee.toFixed(2)} €</TableCell>
                    <TableCell align="center">
                      {fee.amountOfVehiclesFixed ? fee.amountOfVehicles : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {fee.amountOfVehiclesFixed ? `${translate('yes')}` : `${translate('no')}`}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenVehicleTypeDialog(fee)}
                          color="primary"
                        >
                          <Iconify icon="eva:edit-fill" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setVehicleTypeFeeToDelete(fee)}
                          color="error"
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {`${translate('vehicleCostFees.noData')}`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {localVehicleTypeFees.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<Iconify icon="eva:save-fill" />}
              onClick={handleSaveAllVehicleTypeFees}
              sx={{ textTransform: 'none' }}
            >
              {`${translate('vehicleCostFees.saveAll')} (${localVehicleTypeFees.length})`}
            </Button>
          </Stack>
        )}
      </Card>

      {/* Vehicle Type Cost Fee Dialog */}
      <Dialog
        open={openVehicleTypeDialog}
        onClose={handleCloseVehicleTypeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingVehicleTypeFee
            ? `${translate('vehicleCostFees.edit')}`
            : `${translate('vehicleCostFees.new')}`}
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={12}>
              <FormControl fullWidth>
                <InputLabel>{`${translate('vehicleCostFees.vehicleType')}`}</InputLabel>
                <Select
                  value={vehicleTypeFeeFormData.vehicleTypeId || ''}
                  label={`${translate('vehicleCostFees.vehicleType')}`}
                  onChange={(e) =>
                    setVehicleTypeFeeFormData({
                      ...vehicleTypeFeeFormData,
                      vehicleTypeId: Number(e.target.value),
                    })
                  }
                  disabled={!!editingVehicleTypeFee}
                >
                  <MenuItem value="" disabled>
                    {`${translate('vehicleCostFees.selectVehicleType')}`}
                  </MenuItem>
                  {vehicleTypes?.map((vt) => (
                    <MenuItem key={vt.id} value={vt.id}>
                      {vt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={12}>
              <TextField
                fullWidth
                type="number"
                label={`${translate('vehicleCostFees.costFee')}`}
                value={vehicleTypeFeeFormData.costFee || 0}
                onChange={(e) =>
                  setVehicleTypeFeeFormData({
                    ...vehicleTypeFeeFormData,
                    costFee: parseFloat(e.target.value) || 0,
                  })
                }
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid2>

            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!vehicleTypeFeeFormData.amountOfVehiclesFixed}
                    onChange={(e) =>
                      setVehicleTypeFeeFormData({
                        ...vehicleTypeFeeFormData,
                        amountOfVehiclesFixed: e.target.checked,
                        amountOfVehicles: e.target.checked
                          ? vehicleTypeFeeFormData.amountOfVehicles || 0
                          : 0,
                      })
                    }
                  />
                }
                label={`${translate('vehicleCostFees.amountOfVehiclesIsFixed')}`}
              />
            </Grid2>

            <Grid2 size={12}>
              <TextField
                fullWidth
                type="number"
                label={`${translate('vehicleCostFees.amountOfVehicles')}`}
                value={vehicleTypeFeeFormData.amountOfVehicles || 0}
                onChange={(e) =>
                  setVehicleTypeFeeFormData({
                    ...vehicleTypeFeeFormData,
                    amountOfVehicles: parseInt(e.target.value, 10) || 0,
                  })
                }
                disabled={!vehicleTypeFeeFormData.amountOfVehiclesFixed}
                inputProps={{ step: '1', min: '0' }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVehicleTypeDialog} sx={{ textTransform: 'none' }}>
            {`${translate('cancel')}`}
          </Button>
          <Button
            onClick={handleSaveVehicleTypeFee}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {editingVehicleTypeFee
              ? `${translate('save')}`
              : `${translate('vehicleCostFees.addToList')}`}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={vehicleTypeFeeToDelete !== null}
        title={`${translate('delete')}`}
        content={`${translate('vehicleCostFees.deleteConfirm')}`}
        action={
          <Button
            variant="contained"
            color="error"
            loading={isDeletingVehicleTypeFee}
            onClick={handleDeleteVehicleTypeFee}
          >
            {`${translate('delete')}`}
          </Button>
        }
        onClose={() => {
          if (!isDeletingVehicleTypeFee) {
            setVehicleTypeFeeToDelete(null);
          }
        }}
      />
    </Box>
  );
}
